import { UserFieldsFragment as PostListUser } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames';
import { eventNames } from 'constants/eventNames';
import TrackingEvent from 'constants/trackingEvents';
import PubSub from 'pubsub-js';
import { useTranslation } from 'react-i18next';
import { hasModeratorBadge } from 'utils/userHelpers';

import React, { useRef } from 'react';

import { Text } from '@thinkific/chalk/Text';

import { useCommunityContext } from 'components/CommunityContext';
import UserAvatar from 'components/UserAvatar';
import ModeratorBadge from 'components/new/ModeratorBadge';

import * as styles from './UserDetail.css';

interface UserDetailProps {
  author?: PostListUser | null;
  editedAt?: string;
  isEditing?: boolean;
  isFollowing?: boolean;
  isReply?: boolean;
  time: string;
  totalReplyCount?: number;
  isReplyPanel?: boolean;
}

export const UserDetail: React.FC<UserDetailProps> = ({
  author,
  editedAt,
  time,
  isEditing,
  isReply,
  totalReplyCount = 0,
  isFollowing,
  isReplyPanel,
}) => {
  if (!author) return null;

  const userDetailRef = useRef(null);
  const { t } = useTranslation();
  const { trackEvent } = useCommunityContext();
  const userHasModeratorBadge = hasModeratorBadge(author);
  const profileAriaLabel = t(
    'components-userDetail-posted_by_aria_label',
    `Posted by {{fullName}} {{moderator}} {{time}}. Press enter or space to open profile panel`,
    {
      fullName: author.fullName,
      time,
      moderator: userHasModeratorBadge ? t('Moderator') : '',
    }
  );

  const handleUserDetailAction = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.type === 'click') {
      if (isReplyPanel) return;
      event.preventDefault();
      event.stopPropagation();
      PubSub.publish(eventNames.OPEN_PROFILE_PANEL, {
        userId: author.id,
        lastElementRef: userDetailRef,
      });

      trackEvent(TrackingEvent.PROFILE_PANEL_TAB_OPENED, {});
    }
  };

  const extraProps = {
    role: 'button',
    'aria-label': profileAriaLabel,
    tabIndex: 0,
    ref: userDetailRef,
    onClick: handleUserDetailAction,
    onKeyDown: handleUserDetailAction,
  };

  return (
    <div className={styles.userDetail} data-qa="user-detail">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={classNames(styles.wrapper, {
          [styles.cursorPointer]: !isReplyPanel,
        })}
        data-qa="user-detail__wrapper"
        {...extraProps}
      >
        <UserAvatar
          avatarUrl={author.avatarUrl}
          size={isReply ? 'xs' : 'sm'}
          userId={author.id}
          username={author.fullName || ''}
        />
      </div>

      {!isEditing && (
        <div className={styles.userInfo}>
          <div className={classNames(styles.nameContainer, {})}>
            <Text
              as="h3"
              className={styles.name}
              data-qa="main-user-detail-name__link"
              title={author.fullName || ''}
              variant="label.md"
              {...extraProps}
            >
              {author.fullName}
            </Text>

            {userHasModeratorBadge && (
              <ModeratorBadge>{t('Moderator', 'Moderator')}</ModeratorBadge>
            )}
          </div>

          <Text className={styles.postMeta} color="onLight.text.tertiary" variant="body.xs">
            {time}
            {editedAt && <span className={styles.separator}>{t('Edited')}</span>}
            {totalReplyCount > 0 && (
              <span className={styles.separator}>
                {t('{{count}} reply', { count: totalReplyCount })}
              </span>
            )}
            {!isReply && isFollowing && <span className={styles.separator}>{t('Following')}</span>}
          </Text>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
